import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, Button, Table } from 'reactstrap';
import api from '../../services/api';
import NewLeagueWeekModal from './NewLeagueWeekModal';
import EditLeagueWeekModal from './EditLeagueWeekModal';

const LeagueWeeks = ({ league }) => {
    const [weeks, setWeeks] = useState([]);
    const [newWeekModal, setNewWeekModal] = useState(false);
    const [editWeekModal, setEditWeekModal] = useState(false);
    const [editingWeek, setEditingWeek] = useState(null);

    const toggleNewModal = () => setNewWeekModal(!newWeekModal);
    const toggleEditModal = () => setEditWeekModal(!editWeekModal);

    const fetchWeeks = useCallback(async () => {
        try {
            const response = await api.get(`/league-weeks/?league=${league.id}`);
            setWeeks(response.data);
        } catch (error) {
            console.error('Error fetching league weeks:', error);
        }
    }, [league.id]);

    useEffect(() => {
        if (league?.id) {
            fetchWeeks();
        }
    }, [league?.id, fetchWeeks]);

    const handleAddWeek = async (weekData) => {
        try {
            await api.post('/league-weeks/', weekData);
            await fetchWeeks();
            toggleNewModal();
        } catch (error) {
            console.error('Error adding week:', error);
            alert('Error adding week: ' + (error.response?.data?.detail || error.message));
        }
    };

    const handleEditWeek = (week) => {
        setEditingWeek(week);
        toggleEditModal();
    };

    const handleSaveWeek = async (weekData) => {
        try {
            await api.put(`/league-weeks/${editingWeek.id}/`, weekData);
            await fetchWeeks();
            toggleEditModal();
            setEditingWeek(null);
        } catch (error) {
            console.error('Error updating week:', error);
            alert('Error updating week: ' + (error.response?.data?.detail || error.message));
        }
    };

    const handleDeleteWeek = async (weekId) => {
        if (!window.confirm('Are you sure you want to delete this week? This may affect schedule dates.')) {
            return;
        }
        try {
            await api.delete(`/league-weeks/${weekId}/`);
            fetchWeeks();
        } catch (error) {
            console.error('Error deleting week:', error);
            alert('Error deleting week: ' + (error.response?.data?.detail || error.message));
        }
    };

    if (!league?.id) {
        return <p className="text-muted small">Select a league to manage its weeks.</p>;
    }

    return (
        <>
            <Card className="mt-3">
                <CardBody className="pt-3">
                    {weeks.length === 0 ? (
                        <p className="text-muted small">No weeks configured for this league.</p>
                    ) : (
                        <Table bordered hover size="sm" responsive>
                            <thead className="table-light">
                                <tr>
                                    <th style={{ width: '100px' }}>Week #</th>
                                    <th style={{ width: '100px' }}>Par</th>
                                    <th>Notes</th>
                                    <th style={{ width: '150px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {weeks.map((week) => (
                                    <tr key={week.id}>
                                        <td className="text-center fw-semibold">{week.week}</td>
                                        <td className="text-center">{week.par ?? <em className="text-muted">-</em>}</td>
                                        <td>{week.notes || <em className="text-muted">No notes</em>}</td>
                                        <td>
                                            <Button color="primary" size="sm" onClick={() => handleEditWeek(week)}>
                                                Edit
                                            </Button>{' '}
                                            <Button color="danger" size="sm" onClick={() => handleDeleteWeek(week.id)}>
                                                Delete
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                    <div className="mt-3">
                        <Button color="success" size="sm" onClick={toggleNewModal}>
                            + Add Week
                        </Button>
                    </div>
                </CardBody>
            </Card>

            <NewLeagueWeekModal
                isOpen={newWeekModal}
                toggle={toggleNewModal}
                onSave={handleAddWeek}
                leagueId={league?.id}
            />

            <EditLeagueWeekModal
                isOpen={editWeekModal}
                toggle={toggleEditModal}
                onSave={handleSaveWeek}
                week={editingWeek}
                leagueId={league?.id}
            />
        </>
    );
};

export default LeagueWeeks;
